import { debounce, uniqBy } from 'lodash';
import { usePromise } from '../utils/usePromise';
import {
  computed, ComputedRef, onBeforeUnmount, ref, watch, watchEffect
} from '@vue/composition-api';
import { createEventHook } from '@vueuse/core';
import { logicNot } from '@vueuse/math';

import { getIssueChangeRecord, getIssueLabourRecord } from '../api/record';
import { IssueSubTask } from '../models/issue';
import { ChangeRecord } from '../models/record';
import { ActionStore } from './action';
import { FieldsStore } from './fields';
import { IssueStore } from './issue';

export const useChangeRecord = (
  actionStore: ActionStore,
  fieldStore: FieldsStore,
  issueStore: IssueStore,
  isVisitor: ComputedRef<boolean>
) => {
  const recordAddedEvent = createEventHook<ChangeRecord[]>();

  const displayRecord = logicNot(isVisitor);

  const isLabourRecordChanged = ref(false);

  const { result: changeRecordsResponse, refresh: refreshRecords } = usePromise(
    () =>
      displayRecord.value && actionStore.action.value?.type === 'Edit'
        ? getIssueChangeRecord(actionStore.action.value.issueId)
        : null,
    null,
    { immediate: false }
  );

  const { result: labourRecords, refresh: refreshLabourRecord } = usePromise(
    () =>
      displayRecord.value && actionStore.action.value?.type === 'Edit'
        ? getIssueLabourRecord(actionStore.action.value.issueId)
        : [],
    [],
    { immediate: false }
  );

  const changeRecords = computed(
    () => changeRecordsResponse.value?.sysChangeGroupDetailVOList || []
  );

  const debounceRefreshLabourRecord = debounce(refreshLabourRecord, 500);

  watchEffect(() => {
    if (
      displayRecord.value &&
      actionStore.action.value?.type === 'Edit' &&
      fieldStore.fields.value.find((f) => f.fieldCode === 'labourRecord')
        ?.showed === 1
    ) {
      debounceRefreshLabourRecord();
    }
  });

  watch(actionStore.action, () => {
    isLabourRecordChanged.value = false;
  });

  const { off } = fieldStore.onValueSaved((diff) => {
    if (
      Reflect.has(diff, 'labourRecord') &&
      diff.labourRecord[0]?.labour === 0
    ) {
      debounceRefreshLabourRecord();
    }
  });

  onBeforeUnmount(() => {
    off();
  });

  const autoUpdateRecords = ref<ChangeRecord[]>([]);

  recordAddedEvent.on((records) => {
    if (!Array.isArray(records)) {
      return;
    }
    autoUpdateRecords.value = [...records, ...autoUpdateRecords.value];
  });

  const debounceRefreshRecord = debounce(async () => {
    await refreshRecords();
    autoUpdateRecords.value = [];
  }, 500);

  watch(issueStore.isLoadComplete, (complete) => {
    autoUpdateRecords.value = [];
    if (complete && issueStore.issueAssemble.value?.issueVO?.id) {
      refreshRecords();
    }
  });

  const filterRecord = (records: ChangeRecord[] = []) => {
    const issueId = issueStore.issueAssemble.value?.issueVO?.id;
    const issueSubTaskIds =
      fieldStore.fieldValues.value?.subtask?.map((i: IssueSubTask) => i.id) ||
      [];
    const subtaskActionCode = ['status']; // 子任务允许展示记录操作的字段
    return records
      .filter((record) => {
        if (record.objectId === issueId) {
          return true;
        }
        return (
          issueSubTaskIds.includes(record.objectId) &&
          record.changeItemVOS?.some((item) =>
            subtaskActionCode.includes(item.field)
          )
        );
      })
      .map((record) => ({
        ...record,
        isSubtask: record.objectId !== issueId
      }));
  };

  return {
    isShowRecordUpgradeLogo: computed(() =>
      changeRecordsResponse.value
        ? changeRecordsResponse.value.showUpgradeLogo
        : false
    ),
    upgradeDays: computed(() =>
      changeRecordsResponse.value ? changeRecordsResponse.value.days : 0
    ),
    changeRecords: computed(() =>
      displayRecord.value
        ? uniqBy(
          [
            ...filterRecord(autoUpdateRecords.value),
            ...filterRecord(changeRecords.value)
          ],
          'id'
        )
        : []
    ),
    labourRecords,
    refreshLabourRecord: async () => {
      if (
        fieldStore.fields.value.find((f) => f.fieldCode === 'labourRecord')
          ?.showed === 1
      ) {
        await debounceRefreshLabourRecord();
      }
    },
    refreshRecord: debounceRefreshRecord,
    onRecordAdded: recordAddedEvent.on,
    triggerRecordAdded: recordAddedEvent.trigger,
    isLabourRecordChanged
  };
};

export type ChangeRecordStore = ReturnType<typeof useChangeRecord>;
